/**
 * Homeowner invite email, counsel review of Oct 8, 2026.
 * Commercial message. Pacarion LLC is the designated sender.
 * Every interpolated HTML value is escaped. The postal address is
 * PACARION_POSTAL_ADDRESS at send time.
 */

import { siteBase } from './siteUrl';
import emailLayout from './emailLayout';
import { formatPhoneDisplay } from './phoneFormat';
import { formatEmailDate } from '../utils/dates';

function privacyPolicyUrl(base) {
  const root = String(base || siteBase()).replace(/\/$/, '');
  return `${root}/privacy`;
}

const SUPPORT_EMAIL = 'support@pacarion.com';
const INVITE_MAILBOX = 'invites@pacarion.com';
const UNSUBSCRIBE_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=unsubscribe`;

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeHeader(value) {
  return String(value == null ? '' : value).replace(/[\r\n]+/g, ' ').trim().slice(0, 200);
}

function postalAddress(explicit) {
  const value = explicit != null ? explicit : process.env.PACARION_POSTAL_ADDRESS;
  return String(value || '').trim();
}

function inviteLink(code, base) {
  const root = String(base || siteBase()).replace(/\/$/, '');
  return `${root}/login?homeowner_code=${encodeURIComponent(code || '')}`;
}

function displayAgent(agentName) {
  return safeHeader(agentName) || 'Your sponsor';
}

function inviteFrom(agentName) {
  const name = displayAgent(agentName).replace(/[<>"]/g, '');
  return `${name} via Pacarion <${INVITE_MAILBOX}>`;
}

function inviteReplyTo(agentEmail) {
  const email = safeHeader(agentEmail);
  if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return email;
  return SUPPORT_EMAIL;
}

function inviteSubject(agentName) {
  const name = safeHeader(agentName);
  if (!name) return 'Your sponsor invited you to Pacarion';
  return `${name} invited you to Pacarion`;
}

function defaultUnsubscribeUrl(base) {
  const root = String(base || siteBase()).replace(/\/$/, '');
  return `${root}/api/email/unsubscribe`;
}

function listUnsubscribeHeader(httpsUrl, base) {
  const https = String(httpsUrl || defaultUnsubscribeUrl(base));
  return `<${https}>, <${UNSUBSCRIBE_MAILTO}>`;
}

function formatExpiryDate(value) {
  return formatEmailDate(value);
}

function present(value) {
  const trimmed = String(value == null ? '' : value).trim();
  return trimmed || '';
}

function openingSentence(agentName, brokerageName) {
  const agent = displayAgent(agentName);
  const brokerage = present(brokerageName);
  const who = brokerage ? `${agent}, ${brokerage},` : agent;
  return `${who} invited you to Pacarion, an app that helps you keep up with your home's maintenance, appliances, records, and service providers. Your account is free while ${agent} keeps their Pacarion subscription.`;
}

function sharedSentence(agentName, { phone, address, site } = {}) {
  const agent = displayAgent(agentName);
  let shared = 'your name and email address';
  if (phone) shared += ', phone number';
  if (address) shared += ', and property address';
  return `What ${agent} shared: to set up your account, ${agent} gave Pacarion ${shared}. Read how we handle it in our Privacy Policy: ${privacyPolicyUrl(site)}`;
}

function agentLine({ agentName, brokerageName, agentEmail, agentPhone }) {
  const parts = [];
  const brokerage = present(brokerageName);
  parts.push(brokerage ? `${displayAgent(agentName)}, ${brokerage}` : displayAgent(agentName));
  const email = present(agentEmail);
  const phone = formatPhoneDisplay(present(agentPhone)) || present(agentPhone);
  if (email) parts.push(email);
  if (phone) parts.push(phone);
  return parts.join(' · ');
}

function renderInviteEmail({
  clientName,
  clientEmail,
  inviteCode,
  agentName,
  brokerageName,
  agentEmail,
  agentPhone,
  agentWebsite,
  agentHeadshotUrl,
  agentLogoUrl,
  agentTeamName,
  agentProfile,
  unsubscribeUrl,
  postal,
  expiryDate,
  sharedPhone = false,
  sharedAddress = false,
  site,
  sponsorless = false,
}) {
  const name = escapeHtml(clientName);
  const email = escapeHtml(clientEmail);
  const code = escapeHtml(inviteCode);
  const agent = escapeHtml(displayAgent(agentName));
  const link = escapeHtml(inviteLink(inviteCode, site));
  const rawLink = inviteLink(inviteCode, site);
  const unsub = escapeHtml(unsubscribeUrl || '');
  const address = escapeHtml(postalAddress(postal));
  const year = new Date().getFullYear();
  const expiryPlain = expiryDate || formatExpiryDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
  const expiry = escapeHtml(expiryPlain);
  const openingPlain = openingSentence(agentName, brokerageName);
  const opening = escapeHtml(openingPlain);
  const sharedPlain = sharedSentence(agentName, { phone: sharedPhone, address: sharedAddress, site });
  const shared = escapeHtml(sharedPlain);
  const yourAgentPlain = agentLine({ agentName, brokerageName, agentEmail, agentPhone });
  const plainAgent = displayAgent(agentName);
  const privacy = escapeHtml(privacyPolicyUrl(site));

  if (sponsorless) {
    const html = emailLayout.homeownerNotificationShell({
      title: "You're invited to Pacarion",
      agent: null,
      sentTo: clientEmail,
      unsubscribeUrl,
      postal: postalAddress(postal),
      bodyHtml: `
              <div style="display:none; max-height:0; overflow:hidden; mso-hide:all;">Your Pacarion home account is ready to set up.</div>
              <h1 style="margin: 0 0 16px; color: #333; font-size: 24px; font-weight: 500;">You're invited to Pacarion</h1>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">Hi ${name},</p>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">You're invited to Pacarion, an app that helps you keep up with your home's maintenance, appliances, records, and service providers.</p>
              <p style="margin: 0 0 8px; color: #333; font-size: 16px; line-height: 1.6;">To get started:</p>
              <ol style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">
                <li>Click Set Up My Account.</li>
                <li>Choose Homeowner Login and enter your invite code: ${code}</li>
                <li>Review and accept Pacarion's Terms of Service and Privacy Policy.</li>
              </ol>
              <p style="margin: 24px 0;">
                <a href="${link}" style="display: inline-block; padding: 12px 24px; background: #0f766e; color: #ffffff; text-decoration: none; border-radius: 6px; font-size: 16px;">Set Up My Account</a>
              </p>
              <p style="margin: 0 0 8px; color: #333; font-size: 14px; line-height: 1.6;">Or copy this link: ${link}</p>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">Your invite code expires on ${expiry}.</p>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">Don't want an account? You can ignore this email. To have your information removed, email support@pacarion.com.</p>
              <p style="margin: 0; color: #333; font-size: 16px; line-height: 1.6;">Questions about Pacarion? support@pacarion.com</p>
      `,
    });
    const text = [
      'Your Pacarion home account is ready to set up.',
      '',
      "You're invited to Pacarion",
      '',
      `Hi ${clientName || ''},`,
      '',
      "You're invited to Pacarion, an app that helps you keep up with your home's maintenance, appliances, records, and service providers.",
      '',
      'To get started:',
      '1. Click Set Up My Account.',
      `2. Choose Homeowner Login and enter your invite code: ${inviteCode || ''}`,
      "3. Review and accept Pacarion's Terms of Service and Privacy Policy.",
      '',
      `Set Up My Account: ${rawLink}`,
      `Your invite code expires on ${expiryPlain}.`,
      '',
      "Don't want an account? You can ignore this email. To have your information removed, email support@pacarion.com.",
      '',
      'Questions about Pacarion? support@pacarion.com',
      '',
      'This message is from Pacarion LLC.',
      `Pacarion LLC · ${postalAddress(postal)}`,
      `support@pacarion.com`,
      `© ${year} Pacarion LLC · Sent to ${clientEmail || ''}`,
    ].join('\n');
    const httpsUnsub = unsubscribeUrl || defaultUnsubscribeUrl(site);
    return {
      html,
      text,
      subject: "You're invited to Pacarion",
      link: rawLink,
      from: `Pacarion <${INVITE_MAILBOX}>`,
      replyTo: SUPPORT_EMAIL,
      headers: {
        'List-Unsubscribe': listUnsubscribeHeader(httpsUnsub),
        'List-Unsubscribe-Post': 'List-Unsubscribe=One-Click',
      },
    };
  }

  const brandingAgent = agentProfile && typeof agentProfile === 'object' ? {
    ...agentProfile,
    first_name: agentProfile.first_name || agentName,
    brokerage: agentProfile.brokerage || brokerageName,
    email: agentProfile.email || agentEmail,
    phone: agentProfile.phone || agentPhone,
    website: agentProfile.website || agentWebsite,
    headshot_url: agentProfile.headshot_url || agentHeadshotUrl,
    logo_url: agentProfile.logo_url || agentLogoUrl,
    team_name: agentProfile.team_name || agentTeamName,
  } : {
    first_name: agentName,
    brokerage: brokerageName,
    email: agentEmail,
    phone: agentPhone,
    website: agentWebsite,
    headshot_url: agentHeadshotUrl,
    logo_url: agentLogoUrl,
    team_name: agentTeamName,
  };
  const html = emailLayout.notificationShell({
    title: "You're invited to Pacarion",
    agent: brandingAgent,
    personLabel: 'Sponsor',
    footerHtml: `
              <p style="margin: 0 0 8px; color: #666; font-size: 12px; line-height: 1.6;">This invitation is a promotional message from Pacarion LLC, sent on behalf of ${agent}. You're receiving it because ${agent} added you as a client.</p>
              <p style="margin: 0 0 8px; color: #666; font-size: 12px; line-height: 1.6;">Unsubscribe from Pacarion invitations: <a href="${unsub}">${unsub}</a></p>
              <p style="margin: 0 0 8px; color: #666; font-size: 12px; line-height: 1.6;">Pacarion LLC · ${address}</p>
              <p style="margin: 0; color: #666; font-size: 12px; line-height: 1.6;">© ${year} Pacarion LLC · Sent to ${email}</p>
    `,
    bodyHtml: `
              <div style="display:none; max-height:0; overflow:hidden; mso-hide:all;">Your free Pacarion home account is ready to set up.</div>
              <h1 style="margin: 0 0 16px; color: #333; font-size: 24px; font-weight: 500;">You're invited to Pacarion</h1>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">Hi ${name},</p>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">${opening}</p>
              <p style="margin: 0 0 8px; color: #333; font-size: 16px; line-height: 1.6;">To get started:</p>
              <ol style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">
                <li>Click Set Up My Account.</li>
                <li>Choose Homeowner Login and enter your invite code: ${code}</li>
                <li>Review and accept Pacarion's Terms of Service and Privacy Policy.</li>
              </ol>
              <p style="margin: 24px 0;">
                <a href="${link}" style="display: inline-block; padding: 12px 24px; background: #0f766e; color: #ffffff; text-decoration: none; border-radius: 6px; font-size: 16px;">Set Up My Account</a>
              </p>
              <p style="margin: 0 0 8px; color: #333; font-size: 14px; line-height: 1.6;">Or copy this link: ${link}</p>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">Your invite code expires on ${expiry}.</p>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">${shared.replace(privacy, `<a href="${privacy}">${privacy}</a>`)}</p>
              <p style="margin: 0 0 16px; color: #333; font-size: 16px; line-height: 1.6;">Don't want an account? You can ignore this email. To have your information removed, email support@pacarion.com.</p>
              <p style="margin: 0; color: #333; font-size: 16px; line-height: 1.6;">Questions about Pacarion? support@pacarion.com</p>
    `,
  });

  const text = [
    'Your free Pacarion home account is ready to set up.',
    '',
    "You're invited to Pacarion",
    '',
    `Hi ${clientName || ''},`,
    '',
    openingPlain,
    '',
    'To get started:',
    '1. Click Set Up My Account.',
    `2. Choose Homeowner Login and enter your invite code: ${inviteCode || ''}`,
    "3. Review and accept Pacarion's Terms of Service and Privacy Policy.",
    '',
    `Set Up My Account: ${rawLink}`,
    `Or copy this link: ${rawLink}`,
    `Your invite code expires on ${expiryPlain}.`,
    '',
    sharedPlain,
    '',
    "Don't want an account? You can ignore this email. To have your information removed, email support@pacarion.com.",
    '',
    `Your sponsor: ${yourAgentPlain}`,
    'Questions about Pacarion? support@pacarion.com',
    '',
    `This invitation is a promotional message from Pacarion LLC, sent on behalf of ${plainAgent}. You're receiving it because ${plainAgent} added you as a client.`,
    `Unsubscribe from Pacarion invitations: ${unsubscribeUrl || ''}`,
    `Pacarion LLC · ${postalAddress(postal)}`,
    `© ${year} Pacarion LLC · Sent to ${clientEmail || ''}`,
  ].join('\n');

  const httpsUnsub = unsubscribeUrl || defaultUnsubscribeUrl(site);
  return {
    html,
    text,
    subject: inviteSubject(agentName),
    link: rawLink,
    from: inviteFrom(agentName),
    replyTo: inviteReplyTo(agentEmail),
    headers: {
      'List-Unsubscribe': listUnsubscribeHeader(httpsUnsub),
      'List-Unsubscribe-Post': 'List-Unsubscribe=One-Click',
    },
  };
}

function inviteMessage({ agentName, inviteCode, siteBase: base }) {
  const name = present(agentName) || 'Your sponsor';
  const link = inviteLink(inviteCode, base);
  return `Hi, it's ${name}. I've set you up on Pacarion so you can keep up with your home. Open ${link} and enter code ${inviteCode} to get started.`;
}

export {
  privacyPolicyUrl,
  SUPPORT_EMAIL,
  INVITE_MAILBOX,
  UNSUBSCRIBE_MAILTO,
  escapeHtml,
  safeHeader,
  postalAddress,
  inviteLink,
  inviteFrom,
  inviteReplyTo,
  inviteSubject,
  listUnsubscribeHeader,
  formatExpiryDate,
  renderInviteEmail,
  inviteMessage,
};
